import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { closeContract } from "@common/contracts/close.contract";
import { CloseSchema } from "@common/schemas/close.schema";
import { useScope } from "@state/scopeContext.lib";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { SUPPRESS_ERROR_TOAST_HEADER } from "@lib/backendClient";
import { workspaceQueryKeys } from "@api/workspace.api";

const closeClient = getBackendAPIClient(closeContract);

export const closeQueryKeys = {
    all: () => ["closes"] as const,
    organization: (organizationId: string) =>
        [...closeQueryKeys.all(), "organization", organizationId] as const,
    list: (organizationId: string, workspaceId?: string | null) =>
        [
            ...closeQueryKeys.organization(organizationId),
            "list",
            workspaceId ?? "all",
        ] as const,
    detail: (organizationId: string, closeId: string) =>
        [
            ...closeQueryKeys.organization(organizationId),
            "detail",
            closeId,
        ] as const,
};

interface ClosesQueryParams {
    workspaceId: string | null | undefined;
    isEnabled: boolean;
}

function useClosesQuery({ workspaceId, isEnabled }: ClosesQueryParams) {
    const { organizationId } = useScope();
    const activeOrganizationId = isEnabled ? organizationId : null;

    return useQuery({
        queryKey: activeOrganizationId
            ? closeQueryKeys.list(activeOrganizationId, workspaceId)
            : (["closes", "disabled"] as const),
        queryFn: async () => {
            if (!activeOrganizationId) {
                return [];
            }

            const response = await closeClient.listCloses({
                params: { organizationId: activeOrganizationId },
                query: workspaceId ? { workspaceId } : {},
            });

            return response.body.closes.map((c) => CloseSchema.parse(c));
        },
        enabled: !!activeOrganizationId,
    });
}

export function useWorkspaceCloses() {
    const { workspaceId } = useScope();

    return useClosesQuery({ workspaceId, isEnabled: !!workspaceId });
}

export function useOrganizationCloses() {
    return useClosesQuery({ workspaceId: null, isEnabled: true });
}

export function useScopeCloses() {
    const { workspaceId } = useScope();

    return useClosesQuery({ workspaceId, isEnabled: true });
}

export function useDeleteCloseMutation() {
    const queryClient = useQueryClient();
    const { organizationId } = useScope();

    return useMutation({
        mutationFn: async (closeId: string) => {
            if (!organizationId) {
                throw new Error("No org");
            }
            const result = await closeClient.deleteClose({
                params: { organizationId, closeId },
            });
            if (result.status !== 200) {
                throw new Error("Delete failed");
            }
            return result.body;
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({
                queryKey: workspaceQueryKeys.allQueries(),
            });
        },
    });
}

/** Cancels a close; `shouldSuppressErrorToast` leaves failures for the caller to report. */
export function useCancelCloseMutation({
    shouldSuppressErrorToast = false,
}: { shouldSuppressErrorToast?: boolean } = {}) {
    const queryClient = useQueryClient();
    const { organizationId } = useScope();

    return useMutation({
        mutationFn: async (closeId: string) => {
            if (!organizationId) {
                throw new Error("No org");
            }
            const result = await closeClient.cancelClose({
                params: { organizationId, closeId },
                ...(shouldSuppressErrorToast && {
                    extraHeaders: { [SUPPRESS_ERROR_TOAST_HEADER]: "true" },
                }),
            });
            if (result.status !== 200) {
                throw new Error(
                    (result.body as { message?: string }).message ??
                        "Cancel failed",
                );
            }
        },
        onSuccess: (_, closeId) => {
            void queryClient.invalidateQueries({
                queryKey: workspaceQueryKeys.allQueries(),
            });
            if (organizationId) {
                void queryClient.invalidateQueries({
                    queryKey: closeQueryKeys.detail(organizationId, closeId),
                });
            }
        },
    });
}
